import React, { Component } from "react";
import { Editor } from "react-draft-wysiwyg";
import PropTypes from "prop-types";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
} from "draft-js";
import DOMPurify from "dompurify";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

class RichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editorState: EditorState.createEmpty(),
      lastHtml: "",
    };
    this.isInternalChange = false; // track internal user edits
  }

  componentDidMount() {
    const { initialContent } = this.props;
    if (initialContent) this.loadContent(initialContent);
  }

  componentDidUpdate(prevProps) {
    const { initialContent } = this.props;

    // ✅ Prevent resetting editor when editing (only reload if external content changes)
    if (
      initialContent &&
      initialContent !== prevProps.initialContent &&
      !this.isInternalChange &&
      initialContent !== this.state.lastHtml
    ) {
      this.loadContent(initialContent);
    }
  }

  // ✅ Load initial HTML or DraftJS raw data
  loadContent = (content) => {
    try {
      if (!content) return;

      let contentState;
      if (typeof content === "object" && content.blocks) {
        contentState = convertFromRaw(content);
      } else if (typeof content === "string") {
        const blocksFromHtml = htmlToDraft(content);
        if (blocksFromHtml) {
          const { contentBlocks, entityMap } = blocksFromHtml;
          contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap
          );
        }
      }

      if (contentState) {
        const editorState = EditorState.createWithContent(contentState);
        this.setState({
          editorState,
          lastHtml: this.props.initialContent || "",
        });
      }
    } catch (err) {
      console.error("Error loading content:", err);
    }
  };

  // ✅ Called when user types / edits
  onEditorStateChange = (editorState) => {
    this.isInternalChange = true; // mark as user input

    const content = editorState.getCurrentContent();
    const html = draftToHtml(convertToRaw(content));

    this.setState({ editorState, lastHtml: html });

    // 🔥 Debounced Formik or external sync (prevents jump)
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.props.setFieldValue && this.props.name) {
        this.props.setFieldValue(this.props.name, html);
      }
      if (this.props.onChange) {
        this.props.onChange({ html });
      }
      this.isInternalChange = false; // reset flag after sync
    }, 300);
  };

  componentWillUnmount() {
    clearTimeout(this.debounceTimer);
  }
handlePastedText = (text, html, editorState) => {
  if (html) {
    // Remove ALL inline styles, classes, and formatting
    const cleanHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p", "br", "b", "strong", "i", "em", "u",
        "ul", "ol", "li", "a", "blockquote"
      ], // allow only simple formatting
      ALLOWED_ATTR: ["href"],  // remove style="", class="", id="", etc.
    });

    const blocksFromHtml = htmlToDraft(cleanHtml);
    const { contentBlocks, entityMap } = blocksFromHtml;

    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );

    const newEditorState = EditorState.push(
      editorState,
      contentState,
      "insert-characters"
    );

    this.onEditorStateChange(newEditorState);
    return "handled"; // prevent default paste
  }

  return "not-handled";
};
  render() {
    const { editorState } = this.state;
    const { placeholder } = this.props;

    return (
      <div className="editor-container">
        <Editor
          editorState={editorState}
          onEditorStateChange={this.onEditorStateChange}
          wrapperClassName="demo-wrapper"
          editorClassName="demo-editor border p-2 rounded bg-white"
          toolbarClassName="toolbar-class"
          //handlePastedText={this.handlePastedText}
          handlePastedText={() => "not-handled"}
          stripPastedStyles={false}
          placeholder={placeholder || "Type your text here..."}
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "list",
              "textAlign",
              "colorPicker",
              "link",
              "image",
              "remove",
              "history",
            ],
            image: {
              alt: { present: true, mandatory: false },
              previewImage: true,
            },
          }}
        />
      </div>
    );
  }
}

RichTextEditor.propTypes = {
  initialContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  setFieldValue: PropTypes.func,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
};

export default RichTextEditor;
